import { faCircleQuarterStroke } from '@fortawesome/pro-regular-svg-icons'

import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { UserTaxCalculation } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { selectStateEstimateByTaxQuarterAndState } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import { FILING_STATUSES } from '@/features/taxes/taxConstants'
import { TextColors, TypographyStyles } from '@/styles'
import { Card, CardColors, Icon, Separator, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'

import { CalculationRow } from '../estimate-breakdown-v3/shared/calculation-row'
import { QTEFAQs } from '../estimate-breakdown-v3/shared/qte-faqs'
import { PaymentCard } from '../PaymentCard'
import {
  ROUND_UP_INCREMENT_LABEL,
  reconcileEstimateDisplay,
} from '../estimateReconciliation'
import { centsToDollarsOrDashes, centsToEstimateAmount } from '../utils'
import { PlainSummaryCard } from '../PlainSummaryCard'
import { stateSummarySentences } from '../plainSummary'
import { explainZeroStateEstimate } from './explainZeroEstimate'
import { StateIncomeSection } from './StateIncomeSection'
import { StateTaxesPaidSection } from './StateTaxesPaidSection'
import { StateTaxesSection } from './StateTaxesSection'
import { buildStateBreakdownData } from './stateBreakdownData'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

const hasSpouseFilingStatus = (taxCalc: UserTaxCalculation | undefined) =>
  Boolean(
    taxCalc?.filingStatus &&
    [
      FILING_STATUSES.married_filing_jointly,
      FILING_STATUSES.qualifying_widow,
    ].includes(taxCalc.filingStatus)
  )

export const StateBreakdown = ({
  taxCalc,
  estimateDetails,
  filingState,
}: {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  filingState: string | undefined
}) => {
  const quarter = estimateDetails?.taxQuarter
  const data = buildStateBreakdownData(taxCalc, filingState)

  const stateEstimate = useReselector(
    selectStateEstimateByTaxQuarterAndState,
    `${estimateDetails?.taxYear}-${estimateDetails?.taxQuarter}`,
    data?.state
  )

  if (!data) {
    return null
  }

  const reconciliation = reconcileEstimateDisplay({
    calcAmountInCents: data.quarterEstimateInCents,
    releasedAmountInCents: stateEstimate?.estimateInCents,
  })

  const zeroExplanation = explainZeroStateEstimate({ data, quarter })

  return (
    <>
      <Text variant={TypographyStyles.EYEBROW} className="text-muted mb-2">
        {data.state}
      </Text>

      <Text variant={TypographyStyles.H1} className="mb-6">
        Q{quarter} {data.state} Tax Estimate Breakdown
      </Text>

      <div className="flex flex-col lg:flex-row lg:space-x-8">
        <div className="w-full lg:w-2/3 flex flex-col space-y-6">
          <PlainSummaryCard
            sentences={stateSummarySentences({
              data,
              displayInCents: reconciliation.displayInCents,
            })}
          />

          {data.isNoIncomeTaxState ? (
            <Card color={CardColors.LEVEL_1}>
              <div className="flex flex-col space-y-2 p-4">
                <Text variant={TypographyStyles.H3}>
                  {data.state} doesn&apos;t tax self-employment earnings
                </Text>
                <Text color={TextColors.MUTED}>
                  There&apos;s no quarterly estimate to pay {data.state}, so
                  nothing is owed there this quarter.
                </Text>
              </div>
            </Card>
          ) : (
            <>
              <StateIncomeSection data={data} />
              <StateTaxesSection data={data} quarter={quarter} />
              <StateTaxesPaidSection
                data={data}
                quarter={quarter}
                hasSpouse={hasSpouseFilingStatus(taxCalc)}
              />

              <Separator />

              <Card color={CardColors.LEVEL_1}>
                {reconciliation.roundingDeltaInCents !== null && (
                  <CalculationRow
                    left={
                      <Text color={TextColors.MUTED}>
                        Rounded up to the nearest {ROUND_UP_INCREMENT_LABEL}
                        {reconciliation.preRoundingInCents !== null &&
                          ` from ${formatCurrencyFromCents(
                            reconciliation.preRoundingInCents
                          )}`}
                      </Text>
                    }
                    right={
                      <Text color={TextColors.MUTED}>
                        {centsToDollarsOrDashes(
                          reconciliation.roundingDeltaInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}
                <CalculationRow
                  left={
                    <Text variant={TypographyStyles.H2}>
                      <span className="inline-flex items-center">
                        <Icon
                          icon={faCircleQuarterStroke}
                          className="mr-2 transform rotate-[90deg]"
                        />
                        Q{quarter} {data.state} Tax Estimate
                      </span>
                    </Text>
                  }
                  right={
                    <Text variant={TypographyStyles.H3}>
                      {centsToEstimateAmount(reconciliation.displayInCents)}
                    </Text>
                  }
                  inCard
                />
                {reconciliation.releasedAmountDiverges && (
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                    className="mt-2"
                  >
                    This is the amount released for this quarter. It differs
                    from the calculation above, which may have been updated
                    since.
                  </Text>
                )}
                {reconciliation.displayInCents === 0 && zeroExplanation && (
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                    className="mt-2"
                  >
                    {zeroExplanation}
                  </Text>
                )}
              </Card>
            </>
          )}
        </div>

        <div className="w-full lg:w-1/3 flex flex-col space-y-6">
          <Text variant={TypographyStyles.H2}>
            Q{quarter} {data.state} Tax Estimate
          </Text>

          <PaymentCard
            taxCalc={taxCalc}
            estimateDetails={estimateDetails}
            estimateOverride={stateEstimate}
            calcAmountInCents={data.quarterEstimateInCents}
          />

          <QTEFAQs />
        </div>
      </div>
    </>
  )
}
